/**
 * DrawingCanvas Component
 * High-performance canvas with organic brush rendering
 * Rebuilt from scratch with modern React patterns and best practices
 */

import { useRef, useEffect, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import type { BrushSettings, DrawingPoint } from '@/types';
import {
  CANVAS_WIDTH,
  CANVAS_HEIGHT,
  CANVAS_BACKGROUND,
  EXPORT_SCALE,
  EXPORT_BORDER_WIDTH,
} from '@/utils/constants';

/**
 * The ink is always 100% black — matching the app. Pressure (from a stylus) changes only
 * the width of the line, never its darkness.
 */
const INK = '#000000';
const PEN_W_MIN = 0.3; // feather-light press → thin
const PEN_W_MAX = 2.1; // hard press → bold

/** Width multiplier for a point's force; 1 for mouse/finger (pressure < 0). */
function pressureWidthMultiplier(pressure: number): number {
  return pressure >= 0 ? PEN_W_MIN + (PEN_W_MAX - PEN_W_MIN) * pressure : 1;
}

interface DrawingCanvasProps {
  brush: BrushSettings;
  onDrawStart?: () => void;
  onDrawEnd?: () => void;
  /** Sizing classes for the <canvas>. Defaults to 'w-full h-auto' (width-driven). */
  className?: string;
}

export interface DrawingCanvasHandle {
  clear: () => void;
  exportImage: (includeMetadata?: boolean) => string;
  isEmpty: () => boolean;
}

export const DrawingCanvas = forwardRef<DrawingCanvasHandle, DrawingCanvasProps>(
  ({ brush, onDrawStart, onDrawEnd, className }, ref) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const lastPointRef = useRef<DrawingPoint | null>(null);
    const rafIdRef = useRef<number | null>(null);
    const hasDrawnRef = useRef(false);

    /**
     * Initialize canvas with background color
     */
    const initializeCanvas = useCallback(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d', {
        willReadFrequently: false,
        alpha: false,
      });
      if (!ctx) return;

      ctx.fillStyle = CANVAS_BACKGROUND;
      ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    }, []);

    useEffect(() => {
      initializeCanvas();
    }, [initializeCanvas]);

    /**
     * Get canvas coordinates from pointer event
     * Handles both mouse and touch events with proper scaling
     */
    const getPointerPosition = useCallback(
      (event: React.PointerEvent): DrawingPoint | null => {
        const canvas = canvasRef.current;
        if (!canvas) return null;

        const rect = canvas.getBoundingClientRect();
        const { clientX, clientY } = event;
        if (clientX === undefined || clientY === undefined) return null;

        // Scale coordinates from display size to canvas size
        const scaleX = CANVAS_WIDTH / rect.width;
        const scaleY = CANVAS_HEIGHT / rect.height;

        // Real stylus pressure when there's a pen (Apple Pencil, Wacom, S Pen); mouse and
        // finger report no usable force, so they draw at the chosen size (pressure = -1).
        const pressure =
          event.pointerType === 'pen' && event.pressure > 0 && event.pressure <= 1 ? event.pressure : -1;

        return {
          x: (clientX - rect.left) * scaleX,
          y: (clientY - rect.top) * scaleY,
          pressure,
          timestamp: Date.now(),
        };
      },
      []
    );

    /**
     * Calculate dynamic brush parameters based on speed
     */
    const calculateBrushDynamics = useCallback(
      (from: DrawingPoint, to: DrawingPoint, baseSize: number) => {
        const distance = Math.sqrt((to.x - from.x) ** 2 + (to.y - from.y) ** 2);
        const timeDelta = to.timestamp - from.timestamp || 1;
        const speed = distance / timeDelta;

        // Faster strokes are thinner (simulates natural pen behavior)
        const speedFactor = Math.min(speed / 0.5, 1);
        const dynamicSize = baseSize * (1 - speedFactor * 0.3);

        return { distance, dynamicSize };
      },
      []
    );

    /**
     * Render one segment of the ink line — the same brush as the app.
     *
     * The ink is always solid black; how hard you press changes the WIDTH, never the
     * darkness. Character comes from irregularity: dabs wobble off the centre line, their
     * radius varies per dab so the edge is never a clean tube, and the odd fleck of ink
     * lands just outside the edge — so a stroke reads as a loaded brush, not a vector pipe.
     */
    const renderBrushStroke = useCallback(
      (
        ctx: CanvasRenderingContext2D,
        from: DrawingPoint,
        to: DrawingPoint,
        settings: BrushSettings
      ) => {
        const { distance, dynamicSize } = calculateBrushDynamics(from, to, settings.size);
        const dyn = dynamicSize * pressureWidthMultiplier(to.pressure);

        // Unit vector perpendicular to travel — used to wobble the stroke's edges.
        const dist = distance || 0.001;
        const nx = -(to.y - from.y) / dist;
        const ny = (to.x - from.x) / dist;

        const spacing = Math.max(1, settings.size * 0.14); // dense enough to stay continuous
        const steps = Math.min(Math.max(Math.floor(dist / spacing), 1), 96);

        ctx.fillStyle = INK;
        for (let i = 0; i <= steps; i++) {
          const t = i / steps;
          const ix = from.x + (to.x - from.x) * t;
          const iy = from.y + (to.y - from.y) * t;

          // Push each dab off the centre line so the silhouette breathes instead of tubing.
          const wob = (Math.random() - 0.5) * dyn * 0.2;
          const jx = (Math.random() - 0.5) * dyn * 0.12 + nx * wob;
          const jy = (Math.random() - 0.5) * dyn * 0.12 + ny * wob;
          const r = (dyn / 2) * (0.8 + Math.random() * 0.4); // wide variance → ragged, inky edge

          ctx.beginPath();
          ctx.arc(ix + jx, iy + jy, Math.max(r, 0.5), 0, Math.PI * 2);
          ctx.fill();

          // Every so often, a fleck of ink throws off the edge.
          if (Math.random() < 0.07) {
            const side = Math.random() < 0.5 ? 1 : -1;
            const off = (dyn / 2) * (0.85 + Math.random() * 0.55) * side;
            ctx.beginPath();
            ctx.arc(ix + nx * off, iy + ny * off, Math.max(dyn * 0.07, 0.4), 0, Math.PI * 2);
            ctx.fill();
          }
        }

        // Keep a solid spine at the leading edge.
        ctx.beginPath();
        ctx.arc(to.x, to.y, Math.max(dyn / 2.4, 0.5), 0, Math.PI * 2);
        ctx.fill();
      },
      [calculateBrushDynamics]
    );

    /**
     * Land the nib when a stroke starts — a slightly irregular blot, not a perfect circle.
     */
    const drawInitialDot = useCallback(
      (ctx: CanvasRenderingContext2D, point: DrawingPoint, settings: BrushSettings) => {
        const r0 = (settings.size * pressureWidthMultiplier(point.pressure)) / 2;
        ctx.fillStyle = INK;
        ctx.beginPath();
        ctx.arc(point.x, point.y, Math.max(r0 * (0.88 + Math.random() * 0.22), 0.5), 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(
          point.x + (Math.random() - 0.5) * r0 * 0.5,
          point.y + (Math.random() - 0.5) * r0 * 0.5,
          Math.max(r0 * 0.72, 0.4),
          0,
          Math.PI * 2
        );
        ctx.fill();
      },
      []
    );

    /**
     * Handle pointer down (start drawing)
     */
    const handlePointerDown = useCallback(
      (event: React.PointerEvent) => {
        event.preventDefault();
        event.currentTarget.setPointerCapture?.(event.pointerId); // keep the stroke if the pen leaves the canvas
        const point = getPointerPosition(event);
        if (!point) return;

        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!ctx) return;

        setIsDrawing(true);
        hasDrawnRef.current = true;
        lastPointRef.current = point;
        onDrawStart?.();

        // Draw initial dot
        drawInitialDot(ctx, point, brush);
      },
      [brush, getPointerPosition, drawInitialDot, onDrawStart]
    );

    /**
     * Handle pointer move (continue drawing)
     */
    const handlePointerMove = useCallback(
      (event: React.PointerEvent) => {
        if (!isDrawing || !lastPointRef.current) return;

        event.preventDefault();
        const point = getPointerPosition(event);
        if (!point) return;

        // Cancel previous animation frame
        if (rafIdRef.current !== null) {
          cancelAnimationFrame(rafIdRef.current);
        }

        // Schedule rendering on next animation frame
        rafIdRef.current = requestAnimationFrame(() => {
          const canvas = canvasRef.current;
          const ctx = canvas?.getContext('2d');
          if (!ctx || !lastPointRef.current) return;

          renderBrushStroke(ctx, lastPointRef.current, point, brush);
          lastPointRef.current = point;
        });
      },
      [isDrawing, brush, getPointerPosition, renderBrushStroke]
    );

    /**
     * Handle pointer up/leave (stop drawing)
     */
    const handlePointerUp = useCallback(() => {
      if (isDrawing) {
        setIsDrawing(false);
        lastPointRef.current = null;
        onDrawEnd?.();
      }

      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    }, [isDrawing, onDrawEnd]);

    /**
     * Clear canvas and reset state
     */
    const clear = useCallback(() => {
      initializeCanvas();
      hasDrawnRef.current = false;
    }, [initializeCanvas]);

    /**
     * Export canvas as a high-resolution PNG data URL.
     *
     * IMPORTANT (mobile): iOS Safari refuses to allocate a canvas larger than
     * ~16.7M pixels. When that happens toDataURL() does NOT throw — it returns the
     * empty string "data:," — which is how blank/unviewable artworks were sneaking
     * into the queue. To make this robust on every device we:
     *   1. cap the starting scale so the export area stays under a safe ceiling, and
     *   2. retry at progressively smaller scales until the device produces a valid
     *      PNG (verified by inspecting the returned data URL).
     * Worst case we fall all the way back to 1x (500×700) — small, but a REAL image
     * beats a blank "data:," every time. Returns '' only if even 1x fails.
     */
    const exportImage = useCallback((includeMetadata = false): string => {
      const canvas = canvasRef.current;
      if (!canvas) return '';

      const borderSize = includeMetadata ? EXPORT_BORDER_WIDTH : 0;
      const baseWidth = CANVAS_WIDTH + borderSize * 2;
      const baseHeight = CANVAS_HEIGHT + borderSize * 2;

      // Stay well under iOS Safari's ~16.7M px canvas ceiling.
      const MAX_EXPORT_AREA = 16_000_000;
      const maxScale = Math.max(
        1,
        Math.floor(Math.sqrt(MAX_EXPORT_AREA / (baseWidth * baseHeight)))
      );
      const startScale = Math.max(1, Math.min(EXPORT_SCALE, maxScale));

      const isValidPng = (url: string) =>
        typeof url === 'string' && url.startsWith('data:image/') && url.length > 100;

      for (let scale = startScale; scale >= 1; scale--) {
        try {
          const exportCanvas = document.createElement('canvas');
          exportCanvas.width = baseWidth * scale;
          exportCanvas.height = baseHeight * scale;
          const exportCtx = exportCanvas.getContext('2d');
          if (!exportCtx) continue;

          exportCtx.scale(scale, scale);
          exportCtx.imageSmoothingEnabled = true;
          exportCtx.imageSmoothingQuality = 'high';

          if (includeMetadata) {
            // Black matte border behind the drawing.
            exportCtx.fillStyle = '#000000';
            exportCtx.fillRect(0, 0, baseWidth, baseHeight);
            exportCtx.drawImage(canvas, borderSize, borderSize);
          } else {
            exportCtx.drawImage(canvas, 0, 0);
          }

          const url = exportCanvas.toDataURL('image/png');
          if (isValidPng(url)) return url; // success
          // else: device silently refused this size — fall through and retry smaller
          console.warn(
            `[DrawingCanvas] export at ${scale}x produced an invalid capture; retrying smaller`
          );
        } catch (err) {
          console.warn(`[DrawingCanvas] export at ${scale}x threw; retrying smaller`, err);
        }
      }

      console.error('[DrawingCanvas] export failed at every scale (down to 1x)');
      return '';
    }, []);

    /**
     * Check if canvas has been drawn on
     */
    const isEmpty = useCallback(() => {
      return !hasDrawnRef.current;
    }, []);

    // Expose imperative handle to parent
    useImperativeHandle(
      ref,
      () => ({
        clear,
        exportImage,
        isEmpty,
      }),
      [clear, exportImage, isEmpty]
    );

    return (
      <canvas
        ref={canvasRef}
        width={CANVAS_WIDTH}
        height={CANVAS_HEIGHT}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onPointerLeave={handlePointerUp}
        className={`cursor-crosshair rounded block ${className ?? 'w-full h-auto'}`}
        style={{
          touchAction: 'none',
          backgroundColor: CANVAS_BACKGROUND,
          maxWidth: '100%',
          maxHeight: '100%',
          aspectRatio: '5/7',
          display: 'block'
        }}
        aria-label="Drawing canvas"
      />
    );
  }
);

DrawingCanvas.displayName = 'DrawingCanvas';
